import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPinned, Plus, Save } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { Card, CardHeader } from '@/components/ui/card';
import { DataTable } from '@/components/tables/data-table';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { Drawer } from '@/components/ui/drawer';
import { Field } from '@/components/ui/field';
import { useToast } from '@/components/ui/toast';

export function SettingsPage() {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const client = useQueryClient();
  const toast = useToast();
  const query = useQuery({ queryKey: ['admin-settings'], queryFn: () => api('/api/v1/admin/settings').then((result) => result.data) });

  const save = async () => {
    let parsed = value;
    try { parsed = JSON.parse(value); } catch { /* Keep plain text values. */ }
    await api(`/api/v1/admin/settings/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ value: parsed, description: description || undefined }) });
    await client.invalidateQueries({ queryKey: ['admin-settings'] });
    toast.show('Setting saved');
    setOpen(false);
    setKey('');
    setValue('');
    setDescription('');
  };
  const columns = [
    { key: 'key', header: 'Key', cell: (row) => <code className="text-xs text-cyan-200">{row.key}</code> },
    { key: 'value', header: 'Value', cell: (row) => <code className="block max-w-[400px] truncate text-xs text-slate-400">{JSON.stringify(row.value)}</code> },
    { key: 'description', header: 'Description', cell: (row) => <span className="text-xs text-slate-500">{row.description ?? '—'}</span> },
    { key: 'updated', header: 'Updated', sortValue: (row) => row.updatedAt, cell: (row) => <span className="text-[10px] text-slate-600">{new Date(row.updatedAt).toLocaleString()}</span> },
  ];

  return <div className="workspace-page">
    <PageHeader eyebrow="System" title="Settings" description="Runtime configuration and map identity controls." actions={<><Link to="/settings/map-identity" className="inline-flex h-8 items-center gap-2 border border-white/10 bg-white/[.035] px-3 text-[11px] font-bold text-slate-300 transition hover:border-cyan-300/25 hover:text-cyan-100"><MapPinned size={13}/>Map identity</Link><Button className="h-8 px-3 text-xs" onClick={() => setOpen(true)}><Plus size={13}/>New setting</Button></>}/>
    <Card className="workspace-fill"><CardHeader eyebrow="Runtime registry" title="System settings"/>{query.data?.length ? <DataTable tableId="system-settings" rows={query.data} columns={columns} getKey={(row) => row._id} onRefresh={() => query.refetch()} loading={query.isFetching} searchPlaceholder="Search setting or description"/> : <EmptyState title={query.isLoading ? 'Loading settings...' : 'No runtime settings'}/>}</Card>
    <Drawer open={open} onClose={() => setOpen(false)} title="Upsert setting" eyebrow="Runtime configuration">
      <div className="space-y-4"><Field label="Key" required><Input placeholder="setting.key" value={key} onChange={(event) => setKey(event.target.value)}/></Field><Field label="Value" hint="JSON or text" required><Input placeholder='{"enabled":true}' value={value} onChange={(event) => setValue(event.target.value)}/></Field><Field label="Description"><Input placeholder="Short operational note" value={description} onChange={(event) => setDescription(event.target.value)}/></Field><Button className="w-full" disabled={!key.trim()} onClick={() => void save()}><Save size={14}/>Save setting</Button></div>
    </Drawer>
  </div>;
}
